import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { qs, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { monthOptions, monthLabel } from "@/lib/dpc";
import { fmtINR, fmtDay } from "@/lib/service";
import { ServiceCompliance } from "@/components/ServiceCompliance";
import { ServiceCoverage } from "@/components/ServiceCoverage";
import { WorkshopPicker } from "@/components/WorkshopPicker";
import { TargetPanel } from "@/components/TargetPanel";
import { WarrantyToggle, WarrantyStrip, basisText, useExWarranty } from "@/components/WarrantyToggle";
import { WarrantyTrend } from "@/components/WarrantyTrend";
import { BayStrip } from "@/components/service-overview/BayPanel";
import { fmtBay } from "@/components/service-overview/SvcBits";
import { canManageTargets } from "@/lib/nav";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Wrench, Receipt, IndianRupee, ClipboardList, Sparkles, Package, AlertTriangle, Download, Trophy } from "lucide-react";

const TH = "px-2.5 py-2 text-left text-[11px] uppercase tracking-wider font-semibold text-muted-foreground whitespace-nowrap";
const rankTone = (rank, n) => (rank <= 3 ? "bg-emerald-600 text-white" : rank > n - 3 ? "bg-red-600 text-white" : "bg-secondary text-foreground");

function Kpi({ icon: Icon, label, value, sub, testId, tone = "" }) {
  return (
    <div className={`border bg-card rounded-md p-4 ${tone}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold text-muted-foreground"><Icon size={13} /> {label}</div>
      <div className="font-heading text-2xl font-extrabold tracking-tight mt-1 num">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function Leaderboard({ rows, total, mine, ex }) {
  const maxPer = Math.max(...rows.map((r) => r.per_ro), 1);
  const cols = ["#", "Workshop", "FS · PS · RR", "GS ROs", "Revenue ₹", "Revenue / RO", "Gap to best ₹", "GS ROs/bay/day", "BP ROs/bay/day", "Labour/RO", "Parts/RO", "CCS/RO", "ACCS/RO", ...(ex ? [] : ["Warranty/RO"]), "Body shop", "Biggest gap"];
  const bay = (v, rank, needs, testId) => (v == null
    ? <span className={`text-[10px] ${needs ? "text-amber-700" : "text-muted-foreground"}`} title={needs ? "Bays not captured in Admin › Dealers" : "No bays (1S) or no activity"} data-testid={testId}>{needs ? "set bays" : "—"}</span>
    : <span className="num" data-testid={testId}><b>{fmtBay(v)}</b>{rank && <sup className="text-[9px] text-muted-foreground ml-0.5">{rank}</sup>}</span>);
  return (
    <div className="border bg-card rounded-md overflow-auto" data-testid="svc-leaderboard">
      <div className="px-4 py-3 border-b flex items-center gap-2"><Trophy size={14} /><h3 className="font-heading text-sm font-semibold">Workshop leaderboard — GS revenue per RO</h3><span className="text-[11px] text-muted-foreground">GS = Free + Paid + Running Repair · taxable value · {basisText(ex)} · bays = ROs invoiced per bay per active day</span></div>
      <table className="w-full text-xs">
        <thead className="border-b">
          <tr>{cols.map((h) => <th key={h} className={TH}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {[...rows].sort((a, b) => a.rank - b.rank).map((r) => (
            <tr key={r.code} className={`border-b last:border-0 hover:bg-muted/50 ${mine?.includes(r.code) ? "bg-amber-50/60" : ""}`} data-testid={`svc-lb-row-${r.code}`}>
              <td className="px-2.5 py-1.5"><span className={`inline-flex h-5 w-5 items-center justify-center rounded text-[10px] font-bold ${rankTone(r.rank, total)}`} data-testid={`svc-lb-rank-${r.code}`}>{r.rank}</span></td>
              <td className="px-2.5 py-1.5 font-medium whitespace-nowrap"><Link to={`/service/workshop/${r.code}`} className="hover:underline" data-testid={`svc-lb-link-${r.code}`}>{r.code}</Link> <span className="text-muted-foreground font-normal">{r.name}</span>{r.zone && <span className="ml-1 text-[10px] text-muted-foreground">· {r.zone}</span>}</td>
              <td className="px-2.5 py-1.5 num text-muted-foreground whitespace-nowrap">{r.fs} · {r.ps} · {r.rr}</td>
              <td className="px-2.5 py-1.5 num font-semibold">{r.gs}</td>
              <td className="px-2.5 py-1.5 num">{fmtINR(r.revenue)}</td>
              <td className="px-2.5 py-1.5 min-w-[150px]">
                <div className="flex items-center gap-2"><div className="h-2 flex-1 rounded-sm bg-secondary overflow-hidden"><div className="h-full bg-foreground" style={{ width: `${(r.per_ro / maxPer) * 100}%` }} /></div><span className="num font-bold w-14 text-right" data-testid={`svc-lb-per-${r.code}`}>{fmtINR(r.per_ro)}</span></div>
              </td>
              <td className={`px-2.5 py-1.5 num ${r.gap_rev > 0 ? "text-red-600" : "text-emerald-700 font-semibold"}`}>{r.gap_rev > 0 ? fmtINR(r.gap_rev) : "best"}</td>
              <td className="px-2.5 py-1.5 text-right">{bay(r.gs_per_bay_day, r.gs_bay_rank, r.needs_bays, `svc-lb-gsbay-${r.code}`)}</td>
              <td className="px-2.5 py-1.5 text-right">{bay(r.bp_per_bay_day, r.bp_bay_rank, r.needs_bays, `svc-lb-bpbay-${r.code}`)}</td>
              <td className="px-2.5 py-1.5 num">{fmtINR(r.labour_per_ro)}</td>
              <td className="px-2.5 py-1.5 num">{fmtINR(r.parts_per_ro)}</td>
              <td className="px-2.5 py-1.5 num">{fmtINR(r.ccs_per_ro)}</td>
              <td className="px-2.5 py-1.5 num">{fmtINR(r.accs_per_ro)}</td>
              {!ex && <td className="px-2.5 py-1.5 num" data-testid={`svc-lb-wty-${r.code}`}>{fmtINR(r.warranty_per_ro)}</td>}
              <td className="px-2.5 py-1.5 num text-muted-foreground whitespace-nowrap">{r.bs_ros} RO · {fmtINR(r.bs_revenue)}</td>
              <td className="px-2.5 py-1.5 whitespace-nowrap">{r.focus[0] ? <span><span className="rounded bg-red-50 text-red-700 px-1.5 py-0.5 text-[10px] font-semibold">{r.focus[0].label}</span> <span className="num text-muted-foreground">₹{fmtINR(r.focus[0].diff_rev)}</span></span> : <span className="text-muted-foreground">—</span>}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={cols.length} className="px-3 py-6 text-center text-muted-foreground">No workshops in your scope</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function FocusPanel({ rows }) {
  const items = rows.flatMap((r) => r.focus.map((f) => ({ ...f, code: r.code, name: r.name }))).sort((a, b) => b.diff_rev - a.diff_rev).slice(0, 8);
  if (!items.length) return null;
  return (
    <div className="border bg-card rounded-md" data-testid="svc-focus-panel">
      <div className="px-4 py-3 border-b flex items-center gap-2"><Sparkles size={14} /><h3 className="font-heading text-sm font-semibold">Focus areas — where revenue is being left on the table</h3><span className="text-[11px] text-muted-foreground">gap to group-best per RO × own ROs (GS)</span></div>
      <ul className="divide-y">
        {items.map((f, i) => (
          <li key={`${f.code}-${f.cls}`} className="px-4 py-2 flex flex-wrap items-center gap-3 text-xs" data-testid={`svc-focus-${f.code}-${f.cls}`}>
            <span className="text-muted-foreground w-4 num">{i + 1}</span>
            <b className="w-36">{f.code} <span className="font-normal text-muted-foreground">{f.name}</span></b>
            <span className="rounded bg-secondary px-1.5 py-0.5 font-semibold">{f.label}</span>
            <span className="text-muted-foreground">₹{fmtINR(f.per_ro)}/RO vs best ₹{fmtINR(f.best)} · rank {f.rank}</span>
            <span className="ml-auto num font-bold text-red-600">₹{fmtINR(f.diff_rev)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function ServiceHome() {
  const { user } = useApp();
  const [month, setMonth] = useState(monthOptions()[1]);
  const [ex, setEx] = useExWarranty();
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);

  useEffect(() => { api.get("/service/meta").then(({ data }) => setMeta(data)).catch(() => {}); }, []);
  const load = useCallback(() => {
    api.get(`/service/command?${qs({ month, ex_warranty: ex })}`).then(({ data }) => setData(data)).catch((e) => toast.error(apiError(e)));
  }, [month, ex]);
  useEffect(load, [load]);

  const k = data?.kpis;
  return (
    <div className="space-y-6" data-testid="service-home">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">Service Command Center</h1>
          <p className="text-sm text-muted-foreground">{data ? <>{fmtDay(data.from)} → {fmtDay(data.to)} · {data.workshops} workshops · revenue = taxable value by GST invoice date · GS {basisText(data.ex_warranty)}</> : "After-Sales performance across the group"}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <WarrantyToggle ex={ex} onChange={setEx} />
          <WorkshopPicker workshops={meta?.workshops} value="" allLabel="All workshops" />
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="h-8 w-[130px] text-xs" data-testid="svc-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>{monthOptions().map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}</SelectContent>
          </Select>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/service/export?${qs({ month, ex_warranty: ex })}`} className="inline-flex h-8 items-center gap-1.5 rounded-md border px-3 text-xs font-medium hover:bg-secondary transition-colors" data-testid="svc-export-link"><Download size={13} /> Excel</a>
        </div>
      </div>

      {k && (
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-6">
          <Kpi icon={ClipboardList} label="GS ROs" value={fmtINR(k.gs_ros)} sub="FS + PS + RR invoiced" testId="svc-kpi-gs-ros" />
          <Kpi icon={IndianRupee} label={ex ? "GS revenue · ex-wty" : "GS revenue"} value={`₹${fmtINR(k.revenue)}`} sub={ex ? `labour ₹${fmtINR(k.labour_per_ro)} · parts ₹${fmtINR(k.parts_per_ro)} per RO` : `incl. warranty ₹${fmtINR(k.warranty?.total)} · labour ₹${fmtINR(k.labour_per_ro)} · parts ₹${fmtINR(k.parts_per_ro)} per RO`} testId="svc-kpi-revenue" />
          <Kpi icon={Receipt} label="Revenue / RO" value={`₹${fmtINR(k.per_ro)}`} sub={`best workshop ₹${fmtINR(k.best_per_ro)} · gap ₹${fmtINR(k.gap_rev)}`} testId="svc-kpi-per-ro" />
          <Kpi icon={Sparkles} label="CCS / RO" value={`₹${fmtINR(k.ccs_per_ro)}`} sub={`car care ₹${fmtINR(k.ccs_total)} total`} testId="svc-kpi-ccs" />
          <Kpi icon={Package} label="ACCS / RO" value={`₹${fmtINR(k.accs_per_ro)}`} sub={`accessories ₹${fmtINR(k.accs_total)} total`} testId="svc-kpi-accs" />
          <Kpi icon={Wrench} label="Body shop" value={`₹${fmtINR(k.bs_revenue)}`} sub={`${k.bs_ros} accidental ROs`} testId="svc-kpi-bs" />
        </div>
      )}

      {k && <WarrantyStrip w={k.warranty} meta={data.warranty_meta} perRo={k.warranty_per_ro} ex={data.ex_warranty} />}
      {k && <BayStrip bays={k.bays} unset={data.bays_unset} definition={data.bays_definition} />}
      {data && <WarrantyTrend month={month} />}

      {k?.unclassified_revenue > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900 flex items-center gap-2" data-testid="svc-unclassified-banner">
          <AlertTriangle size={14} /> ₹{fmtINR(k.unclassified_revenue)} of revenue sits on codes no rule recognises (counted as regular).
          {user?.role === "admin" && <Link to="/admin?tab=service" className="underline font-semibold">Classify them in Admin › Service</Link>}
        </div>
      )}

      {data && <TargetPanel month={month} myCodes={data.scope || []} showRace canManage={canManageTargets(user)} exWarranty={ex} />}
      {data && <Leaderboard rows={data.rows} total={data.workshops} mine={data.scope} ex={data.ex_warranty} />}
      {data && <FocusPanel rows={data.rows} />}

      <details className="border bg-card rounded-md" data-testid="svc-datafeed">
        <summary className="px-4 py-3 cursor-pointer text-sm font-heading font-semibold select-none">Data feed — coverage & daily upload compliance</summary>
        <div className="p-4 space-y-4 border-t">
          <ServiceCoverage month={month} />
          <ServiceCompliance month={month} />
        </div>
      </details>
    </div>
  );
}
